import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MIN_AGE = 21;

function ageFromDob(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr + "T00:00:00");
  if (Number.isNaN(dob.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - dob.getFullYear();
  const m = t.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < dob.getDate())) a--;
  return a;
}

export default function Signup() {
  const [f, setF] = useState({ name: "", email: "", password: "", dob: "", ageConfirmed: false });
  const [busy, setBusy] = useState(false);
  const { signup } = useAuth();
  const nav = useNavigate();

  const age = useMemo(() => ageFromDob(f.dob), [f.dob]);
  const dobInvalid = f.dob && age !== null && age < MIN_AGE;
  const maxDob = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - MIN_AGE);
    return d.toISOString().slice(0, 10);
  }, []);

  const canSubmit = f.name && f.email && f.password.length >= 6 && f.dob && !dobInvalid && f.ageConfirmed;

  const submit = async (e) => {
    e.preventDefault();
    if (dobInvalid) {
      toast.error(`You must be at least ${MIN_AGE} to create an account.`);
      return;
    }
    if (!f.ageConfirmed) {
      toast.error(`Please confirm you are ${MIN_AGE}+ and agree to our Terms.`);
      return;
    }
    setBusy(true);
    try {
      await signup(f.name, f.email, f.password, f.dob, f.ageConfirmed);
      toast.success("Profile created — complete your questionnaire next.");
      nav("/questionnaire");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    }
    setBusy(false);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="signup-page">
      <h1 className="font-heading text-4xl mb-2">Create your account.</h1>
      <p className="text-neutral-600 mb-8">Get first look at new events, save the ones you love, and never miss a night. You must be {MIN_AGE}+ to join.</p>
      <Button onClick={googleLogin} variant="outline" className="w-full rounded-full mb-4" data-testid="google-signup-btn">Continue with Google</Button>
      <div className="text-center text-eyebrow my-4">or</div>
      <form onSubmit={submit} className="space-y-4">
        <Input placeholder="Full name" value={f.name} onChange={e=>setF({...f, name: e.target.value})} required data-testid="signup-name" className="rounded-xl" />
        <Input type="email" placeholder="Email" value={f.email} onChange={e=>setF({...f, email: e.target.value})} required data-testid="signup-email" className="rounded-xl" />
        <Input type="password" placeholder="Password (min 6 chars)" value={f.password} onChange={e=>setF({...f, password: e.target.value})} required minLength={6} data-testid="signup-password" className="rounded-xl" />
        <div>
          <label htmlFor="signup-dob" className="block text-sm text-neutral-600 mb-1.5">Date of birth <span className="text-neutral-400">(you must be {MIN_AGE}+)</span></label>
          <Input
            id="signup-dob"
            type="date"
            max={maxDob}
            value={f.dob}
            onChange={e=>setF({...f, dob: e.target.value})}
            required
            data-testid="signup-dob"
            className="rounded-xl"
          />
          {dobInvalid && (
            <p className="mt-1.5 text-sm text-red-600" data-testid="signup-dob-error">
              You must be at least {MIN_AGE} to create an account.
            </p>
          )}
        </div>
        <label className="flex items-start gap-3 text-sm text-neutral-700 cursor-pointer">
          <input
            type="checkbox"
            checked={f.ageConfirmed}
            onChange={e=>setF({...f, ageConfirmed: e.target.checked})}
            required
            data-testid="signup-age-checkbox"
            className="mt-1 h-4 w-4 rounded border-neutral-400 accent-black"
          />
          <span>
            I confirm I am {MIN_AGE} years or older and agree to the{" "}
            <Link to="/terms" className="underline">Terms &amp; Conditions</Link>{" "}
            and{" "}
            <Link to="/safety" className="underline">Safety Statement</Link>.
          </span>
        </label>
        <Button type="submit" disabled={busy || !canSubmit} className="w-full rounded-full bg-black text-white hover:bg-neutral-800 btn-pill" data-testid="signup-submit">
          {busy ? "Creating…" : "Create profile"}
        </Button>
      </form>
      <div className="mt-6 text-sm text-neutral-600 text-center">
        Already a member? <Link to="/login" className="underline">Log in</Link>
      </div>
    </div>
  );
}
